import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { ShadowBox, Box, Text, Tooltip } from '@components/common';
import { TableHeading } from '@components/Typography';
import TableAccessaryLabel from '@components/TableAccessaryLabel';
import SvgIcon from '@components/SvgIcon';
import { stripePriceConverter } from '@services/transform';

import { BillingHistorySelectors } from '@redux/BillingHistoryRedux';
import { COUPON_TYPES } from '@services/minte';

const CleanButton = styled(TableAccessaryLabel)`
  width: 102px;
  max-width: 102px;
`;

const ItemRow = styled(Box)`
  box-shadow: 0px 1px 0px 0px rgba(0, 0, 0, 0.1);
`;

function BillingHistoryDetail(props) {
  const { billingList } = props;

  if (!billingList) {
    return null;
  }

  const columns = [
    { title: 'Service Type', align: 'flex-start' },
    {
      title: 'Additional Services',
      align: 'flex-start'
    },
    { title: 'Original Price', align: 'center' },
    {
      title: 'Discounts',
      align: 'center'
    },
    { title: 'Final Cost', align: 'center' },
    { title: 'Service Date', align: 'center' },
    { title: 'Payment Date', align: 'center' }
  ];

  const renderHeader = () => {
    return (
      <ItemRow
        flex={1}
        display="flex"
        flexDirection="row"
        justifyContent="center"
        alignItems="center"
        py={4}
        mb={4}
      >
        {columns.map(({ title: item, align }) => (
          <Box
            key={item}
            flex={1}
            display="flex"
            justifyContent={align}
            alignItems="center"
          >
            <TableHeading>{item}</TableHeading>
          </Box>
        ))}
      </ItemRow>
    );
  };

  const renderHistoryItem = (historyItem, index) => {
    const {
      cost: receiptDetails,
      defaultFormattedDate,
      additionalService,
      serviceType,
      transactions
    } = historyItem;

    const {
      formattedTransactionDate,
      price: total,
      status: transactionStatus
    } = transactions;

    const {
      beforeDiscountCost,
      couponDetails,
      discountDetails: recurringDiscount
    } = receiptDetails || {};

    const { title, icon } = serviceType;

    const {
      off: couponOff,
      valueOff: couponValue,
      type: couponType,
      coupon: couponName
    } = couponDetails || {};
    const { off: recurringDiscountValue, type: recurringDiscountType } =
      recurringDiscount || {};

    return (
      <ItemRow
        flex={1}
        display="flex"
        flexDirection="row"
        justifyContent="center"
        alignItems="flex-end"
        pb={4}
        mb={4}
        key={index}
      >
        <Box
          flex={1}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <CleanButton
            p={1}
            flexDirection="row"
            justifyContent="space-between"
            title={title}
            icon={icon}
            tooltip={title}
            size="small"
            onClick={() => {}}
          />
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          {additionalService &&
            additionalService.length > 0 &&
            additionalService.map(addon => {
              const { name, icon: addonIcon, title } = addon;

              return (
                <Tooltip
                  key={name}
                  animation="scale"
                  delay={[300, 0]}
                  duration={300}
                  content={title}
                  theme="light-border"
                  placement="bottom"
                >
                  <span>
                    <SvgIcon
                      color="gold"
                      mr={1}
                      key={addonIcon}
                      name={addonIcon}
                      width={16}
                      height={16}
                    />
                  </span>
                </Tooltip>
              );
            })}
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontSize={[0, 0, 1]}>{`$${stripePriceConverter(
            beforeDiscountCost
          )}`}</Text>
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="flex-start"
          flexDirection="column"
          alignItems="center"
        >
          {!isNaN(couponValue) && (
            <React.Fragment>
              <Text fontSize={[0, 0, 1]}>
                {couponType === COUPON_TYPES.FIXED_AMOUNTINGLE
                  ? `$${stripePriceConverter(couponValue)}`
                  : `${couponOff}%`}
              </Text>
              <Text fontSize={[0, 0, 1]}>({couponName})</Text>
            </React.Fragment>
          )}

          {!isNaN(recurringDiscountValue) && (
            <React.Fragment>
              <Text fontSize={[0, 0, 1]}>
                {recurringDiscountType === COUPON_TYPES.FIXED_AMOUNTINGLE
                  ? `$${stripePriceConverter(recurringDiscountValue)}`
                  : `${recurringDiscountValue}%`}
              </Text>
              <Text fontSize={[0, 0, 1]}>(Package) </Text>
            </React.Fragment>
          )}
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontSize={[0, 0, 1]}>{`$${total}`}</Text>
        </Box>

        <Box
          flex={1}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Text fontSize={[0, 0, 1]}>{defaultFormattedDate}</Text>
        </Box>

        <Box
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Text align="center" fontSize={[0, 0, 1]}>
            {formattedTransactionDate}
          </Text>
          <Text align="center" fontSize={[0, 0, 1]}>
            ({transactionStatus})
          </Text>
        </Box>
      </ItemRow>
    );
  };

  return (
    <ShadowBox p={4}>
      {renderHeader()}
      {billingList.map(renderHistoryItem)}
    </ShadowBox>
  );
}

BillingHistoryDetail.propTypes = {
  billingList: PropTypes.array
};

const mapStatesToProps = state => ({
  billingList: BillingHistorySelectors.selectBillingHistory(state)
});

export default connect(mapStatesToProps)(BillingHistoryDetail);
